// Pausa planejada do banco, do lado da aba aberta. O servidor (Middlewares::PlannedMaintenance)
// expõe o estado em /maintenance/status e responde 503 com `X-Hospedin-Maintenance: 1` a todo
// request novo durante a pausa. Aqui a aba:
//
// - avisa nos 5 minutos antes do horário agendado e se fecha sozinha nesse horário, pelo relógio
//   do servidor;
// - durante a pausa, segura cliques, envios de formulário e requests para o próprio PMS, sem
//   recarregar nem desmontar a página, para não perder o que foi preenchido;
// - volta quando o status diz `normal`. Só o Rails responde esse status, e o operador só retoma
//   depois da readiness strict saudável, então a aba não repete essa checagem: milhares de abas
//   batendo no banco que acabou de voltar seria pior que esperar. A volta de cada aba é espalhada
//   em até 2s pelo mesmo motivo;
// - leituras (GET) do axios recusadas pela pausa repetem uma vez depois da volta. Escritas nunca
//   são repetidas: uma escrita que falhou com a manutenção anunciada deixa um aviso para o usuário
//   conferir o resultado.
//
// O status não vem de uma consulta periódica: o servidor manda o estado no header de toda resposta
// (`x-hospedin-maintenance-state`) e no meta `planned-maintenance-status` da página, e o mesmo
// Middlewares::PlannedMaintenance grava esse estado no env lido pelo layout. Então nenhum request é
// gasto para descobrir que está tudo normal, e o CloudFront/WAF (que cobram por request, com cache
// hit ou não) não cobra por essa descoberta.
//
// **A janela é o gate.** Sem manutenção marcada a aba não consulta nada: nem timer, nem foco, nem
// gesto. O que liga o acompanhamento é a manutenção marcada — o pedido explícito do operador, com
// pelo menos 6 min de antecedência —, não um horário fixo no cliente. Horário fixo quebraria numa
// janela fora do habitual (a de produção é de madrugada, mas nada impede uma de tarde) e exigiria
// uma flag para forçar. As consultas a /maintenance/status ficam em três casos, limitados pela
// janela e não pelo número de abas abertas:
//
// 1. primeira carga sem o meta (HTML antigo em cache): uma consulta; e a aba que nunca confirmou
//    estado nenhum tenta de novo ao voltar à frente, até conseguir;
// 2. manutenção marcada (15 s) ou em pausa (3 s), e o foco de uma aba que já sabe da janela;
//
// O aviso dos 5 minutos pode então chegar na primeira resposta da aba, em vez de antes dela. A
// pausa nunca depende disso: um 503 marcado já a detecta em qualquer request.
//
// Com a consulta a cada 15 s em toda aba aberta, eram ~200 req/s em prod (outubro/2026); com 60 s,
// 3,26 M requests/dia, ou 57% de todo o tráfego do PMS e ~US$ 299/mês na borda. Ver
// docs/infra/maintenance-status-sem-poll.md.
import type { AxiosInstance } from "axios";
import { createMaintenanceView, type MaintenanceView } from "./planned-maintenance-view";

export type MaintenancePhase = "normal" | "scheduled" | "paused";
export type MaintenanceStatus = {
  phase: MaintenancePhase;
  starts_at: string | null;
  server_now: string;
  duration_seconds: number;
};

export const MAINTENANCE_HEADER = "X-Hospedin-Maintenance";
export const STATE_HEADER = "X-Hospedin-Maintenance-State";
export const STARTS_AT_HEADER = "X-Hospedin-Maintenance-Starts-At";
export const DURATION_HEADER = "X-Hospedin-Maintenance-Duration";
export const SERVER_NOW_HEADER = "X-Hospedin-Maintenance-Now";
export const STATUS_META = 'meta[name="planned-maintenance-status"]';
const STATUS_URL = "/maintenance/status";
const DEFAULT_DURATION_SECONDS = 40;
// Por fase: sem manutenção marcada não há timer (o estado chega nas respostas e no meta); marcada,
// 15 s para pegar remarcação e cancelamento; na pausa, 3 s para voltar logo.
const SCHEDULED_POLL_MS = 15_000;
const PAUSED_POLL_MS = 3_000;
const POLL_JITTER_MS = 2_000;
const RECOVERY_JITTER_MS = 2_000;
const STATUS_TIMEOUT_MS = 5_000;
const BANNER_LEAD_MS = 5 * 60_000;
const PAUSED_MESSAGE = "Sistema em manutenção";

// O axios não entra neste bundle em runtime. Importar `CanceledError` puxava o axios inteiro
// (58 KB minificados, 22 KB gzip) para páginas que não o usam, como o login. `axios.isCancel` só lê
// `__CANCEL__`, então um erro local basta para o interceptor e para `describeRequestFailure`
// tratarem a recusa como cancelamento.
class MaintenanceCanceledError extends Error {
  __CANCEL__ = true;
  code = "ERR_CANCELED";

  constructor(message = PAUSED_MESSAGE) {
    super(message);
    this.name = "CanceledError";
  }
}

type HeaderGetter = (name: string) => string | null | undefined;

export function parseMaintenanceStatus(value: unknown): MaintenanceStatus {
  const status = value as Partial<MaintenanceStatus> | null;
  const valid =
    !!status &&
    ["normal", "scheduled", "paused"].includes(status.phase as string) &&
    Number.isFinite(Date.parse(status.server_now ?? "")) &&
    Number.isInteger(status.duration_seconds) &&
    (status.duration_seconds as number) > 0 &&
    (status.phase !== "scheduled" || Number.isFinite(Date.parse(status.starts_at ?? "")));
  if (!valid) throw new Error("Status de manutenção inválido");
  return status as MaintenanceStatus;
}

const isSafeMethod = (method = "GET") => /^(GET|HEAD|OPTIONS)$/i.test(method);
const isSameOrigin = (url: string) => new URL(url, location.href).origin === location.origin;

type Waiter = { resolve: () => void; reject: () => void };

export type MaintenanceController = {
  pause: () => void;
  isPaused: () => boolean;
  isAnnounced: () => boolean;
  hasUncertainWrite: () => boolean;
  observeResponse: (method: string, status: number, marker: string | null, announcedAtStart: boolean) => void;
  observeStatusHeaders: (get: HeaderGetter) => void;
  waitForRecovery: (signal?: AbortSignal) => Promise<void>;
  cleanup: () => void;
};

let active: MaintenanceController | undefined;

export const isMaintenancePaused = () => active?.isPaused() ?? false;
// O payments.js recarrega a página depois de alguns fluxos; com manutenção anunciada ou uma
// escrita incerta, recarregar apagaria o aviso e o que estiver preenchido.
export const canMaintenanceReload = () => !(active?.isAnnounced() || active?.hasUncertainWrite());

export function installPlannedMaintenance(
  options: { fetcher?: typeof fetch; view?: MaintenanceView; logoUrl?: string | null } = {},
): MaintenanceController {
  if (active) return active;

  const originalFetch = window.fetch;
  const fetcher = options.fetcher ?? originalFetch.bind(window);
  const view = options.view ?? createMaintenanceView({ logoUrl: options.logoUrl });
  const lifetime = new AbortController();
  const waiters = new Set<Waiter>();

  let phase: MaintenancePhase = "normal";
  let duration = DEFAULT_DURATION_SECONDS;
  // Quando o servidor confirmou o estado atual, pelo relógio local. 0 = nunca confirmado.
  let confirmedAt = 0;
  let uncertain = false;
  let disposed = false;
  let polling = false;
  // Muda a cada novo sinal de pausa e invalida uma volta já agendada.
  let pauseGeneration = 0;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let cutoffTimer: ReturnType<typeof setTimeout> | undefined;
  let recoveryTimer: ReturnType<typeof setTimeout> | undefined;

  function pause() {
    if (disposed) return;
    pauseGeneration += 1;
    clearTimeout(cutoffTimer);
    clearTimeout(recoveryTimer);
    recoveryTimer = undefined;
    if (phase === "paused") return;
    phase = "paused";
    view.showOverlay(duration);
    schedulePoll();
  }

  function resume(next: Exclude<MaintenancePhase, "paused">) {
    phase = next;
    view.hideOverlay();
    for (const waiter of waiters) waiter.resolve();
    waiters.clear();
  }

  function recoverSoon() {
    if (recoveryTimer) return;
    const generation = pauseGeneration;
    recoveryTimer = setTimeout(() => {
      recoveryTimer = undefined;
      if (!disposed && phase === "paused" && generation === pauseGeneration) resume("normal");
    }, Math.random() * RECOVERY_JITTER_MS);
  }

  function apply(status: MaintenanceStatus) {
    duration = status.duration_seconds;
    if (status.phase === "paused") return pause();

    if (status.phase === "normal") {
      if (phase === "paused") return recoverSoon();
      phase = "normal";
      view.hideBanner();
      return;
    }

    const startsAt = new Date(status.starts_at as string);
    const remaining = startsAt.getTime() - Date.parse(status.server_now);
    if (remaining <= 0) return pause();

    // Reagendada para depois: a aba que já tinha se fechado no horário antigo volta a liberar.
    if (phase === "paused") resume("scheduled");
    else phase = "scheduled";
    clearTimeout(cutoffTimer);
    cutoffTimer = setTimeout(pause, remaining);
    if (remaining <= BANNER_LEAD_MS) view.showBanner(startsAt, duration);
    else view.hideBanner();
  }

  async function poll() {
    if (polling || disposed) return;
    // Aba escondida fora da pausa espera voltar à frente (refresh). Na pausa continua, para soltar
    // as leituras que esperam a volta.
    if (document.visibilityState === "hidden" && phase !== "paused") return;
    polling = true;
    clearTimeout(pollTimer);
    const request = new AbortController();
    const abort = () => request.abort();
    lifetime.signal.addEventListener("abort", abort, { once: true });
    const timeout = setTimeout(abort, STATUS_TIMEOUT_MS);
    try {
      const response = await fetcher(STATUS_URL, {
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: request.signal,
      });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const status = parseMaintenanceStatus(await response.json());
      confirmedAt = Date.now();
      if (!disposed) apply(status);
    } catch {
      // Sem resposta com a manutenção já anunciada, a aba se fecha: melhor esperar do que
      // mandar uma escrita para um banco que pode estar caindo. Fora disso, é só rede ruim.
      if (phase !== "normal") pause();
    } finally {
      clearTimeout(timeout);
      lifetime.signal.removeEventListener("abort", abort);
      polling = false;
      schedulePoll();
    }
  }

  function schedulePoll() {
    if (disposed) return;
    clearTimeout(pollTimer);
    // Sem manutenção marcada não há timer: o estado chega no header de toda resposta e no meta da
    // página, e a janela em si (scheduled/paused) é o gate.
    if (phase === "normal") return;
    const interval = phase === "paused" ? PAUSED_POLL_MS : SCHEDULED_POLL_MS;
    pollTimer = setTimeout(poll, interval + Math.random() * POLL_JITTER_MS);
  }

  // Estado vindo de header (ou do meta) no formato que `apply` já entende. Header ausente ou
  // inválido (proxy que filtra, resposta de outro serviço) simplesmente não diz nada.
  function statusFrom(get: HeaderGetter): MaintenanceStatus | null {
    const next = get(STATE_HEADER);
    if (next !== "normal" && next !== "scheduled" && next !== "paused") return null;
    if (next === "normal")
      return { phase: "normal", starts_at: null, server_now: new Date().toISOString(), duration_seconds: duration };
    const seconds = Number(get(DURATION_HEADER));
    try {
      return parseMaintenanceStatus({
        phase: next,
        starts_at: get(STARTS_AT_HEADER) ?? null,
        server_now: get(SERVER_NOW_HEADER),
        duration_seconds: Number.isInteger(seconds) ? seconds : duration,
      });
    } catch {
      return null;
    }
  }

  function observeStatusHeaders(get: HeaderGetter) {
    if (disposed) return;
    const status = statusFrom(get);
    if (!status) return;
    confirmedAt = Date.now();
    apply(status);
  }

  function observeResponse(method: string, status: number, marker: string | null, announcedAtStart: boolean) {
    if (status === 503 && marker === "1") {
      pause();
      return;
    }
    const announced = announcedAtStart || phase !== "normal";
    if (!isSafeMethod(method) && announced && (status === 0 || status >= 500)) {
      uncertain = true;
      view.showUncertain();
    }
  }

  function waitForRecovery(signal?: AbortSignal): Promise<void> {
    if (disposed || signal?.aborted) return Promise.reject(new MaintenanceCanceledError());
    if (phase !== "paused") return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        waiters.delete(waiter);
        waiter.reject();
      };
      const waiter: Waiter = {
        resolve: () => {
          signal?.removeEventListener("abort", onAbort);
          resolve();
        },
        reject: () => {
          signal?.removeEventListener("abort", onAbort);
          reject(new MaintenanceCanceledError());
        },
      };
      signal?.addEventListener("abort", onAbort, { once: true });
      waiters.add(waiter);
    });
  }

  // Interações com a página ficam bloqueadas na pausa; as do próprio aviso, não.
  const block = (event: Event) => {
    if (phase !== "paused" || view.contains(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    view.focusOverlay();
  };
  const blockedEvents = ["click", "submit", "keydown"];
  for (const name of blockedEvents) document.addEventListener(name, block, true);

  // `form.submit()` não dispara o evento submit, então o bloqueio acima não pega.
  const formPrototype = HTMLFormElement.prototype;
  const originalSubmit = formPrototype.submit;
  const originalRequestSubmit = formPrototype.requestSubmit;
  formPrototype.submit = function () {
    if (phase !== "paused") originalSubmit.call(this);
  };
  formPrototype.requestSubmit = function (submitter) {
    if (phase !== "paused") originalRequestSubmit.call(this, submitter);
  };

  // fetch e XHR crus (jQuery, scripts legados) passam pelas mesmas regras do axios.
  window.fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (!isSameOrigin(url)) return originalFetch.call(window, input, init);
    if (phase === "paused") throw new MaintenanceCanceledError(PAUSED_MESSAGE);
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const announcedAtStart = phase !== "normal";
    try {
      const response = await originalFetch.call(window, input, init);
      observeStatusHeaders((name) => response.headers.get(name));
      observeResponse(method, response.status, response.headers.get(MAINTENANCE_HEADER), announcedAtStart);
      return response;
    } catch (error) {
      observeResponse(method, 0, null, announcedAtStart);
      throw error;
    }
  };

  const xhrPrototype = XMLHttpRequest.prototype;
  const originalOpen = xhrPrototype.open;
  const originalSend = xhrPrototype.send;
  const xhrRequests = new WeakMap<XMLHttpRequest, { method: string; local: boolean }>();
  xhrPrototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    xhrRequests.set(this, { method, local: isSameOrigin(String(url)) });
    originalOpen.call(this, method, url, async ?? true, username, password);
  };
  xhrPrototype.send = function (body) {
    const request = xhrRequests.get(this);
    if (request?.local) {
      if (phase === "paused") throw new MaintenanceCanceledError(PAUSED_MESSAGE);
      const announcedAtStart = phase !== "normal";
      this.addEventListener(
        "loadend",
        () => {
          observeStatusHeaders((name) => this.getResponseHeader(name));
          observeResponse(request.method, this.status, this.getResponseHeader(MAINTENANCE_HEADER), announcedAtStart);
        },
        { once: true },
      );
    }
    originalSend.call(this, body);
  };

  // Aba que já sabe de uma janela (marcada ou em pausa) confere ao voltar à frente: são os minutos
  // da janela, não o dia inteiro. Aba em `normal` não consulta — o estado chega na próxima resposta
  // que ela fizer. A exceção é a aba que nunca confirmou estado nenhum (carga sem o meta, rede
  // fora): ela tenta de novo aqui até conseguir uma resposta.
  const refresh = () => {
    if (document.visibilityState === "hidden") return;
    if (phase === "normal" && confirmedAt !== 0) return;
    void poll();
  };
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", refresh);
  // `pageshow` só interessa na volta do BFCache: o que segue toda carga não é a aba voltando, e o
  // estado acabou de chegar no meta.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) refresh();
  });

  const controller: MaintenanceController = {
    pause,
    isPaused: () => phase === "paused",
    isAnnounced: () => phase !== "normal",
    hasUncertainWrite: () => uncertain,
    observeResponse,
    observeStatusHeaders,
    waitForRecovery,
    cleanup() {
      if (disposed) return;
      disposed = true;
      lifetime.abort();
      for (const waiter of waiters) waiter.reject();
      waiters.clear();
      clearTimeout(pollTimer);
      clearTimeout(cutoffTimer);
      clearTimeout(recoveryTimer);
      for (const name of blockedEvents) document.removeEventListener(name, block, true);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      formPrototype.submit = originalSubmit;
      formPrototype.requestSubmit = originalRequestSubmit;
      window.fetch = originalFetch;
      xhrPrototype.open = originalOpen;
      xhrPrototype.send = originalSend;
      view.destroy();
      active = undefined;
    },
  };
  // A página já chega sabendo o estado pelo meta do layout: sem consulta na primeira carga.
  function bootstrapFromMeta(): boolean {
    const content = document.querySelector<HTMLMetaElement>(STATUS_META)?.content;
    if (!content) return false;
    try {
      const status = parseMaintenanceStatus(JSON.parse(content));
      confirmedAt = Date.now();
      apply(status);
      schedulePoll();
      return true;
    } catch {
      return false;
    }
  }

  active = controller;
  if (!bootstrapFromMeta()) void poll();
  return controller;
}

export function installMaintenanceAxios(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    if (isMaintenancePaused() && isSameOrigin(instance.getUri(config)))
      throw new MaintenanceCanceledError(PAUSED_MESSAGE);
    return config;
  });
  const axiosHeaderReader = (headers: unknown): HeaderGetter => {
    const source = headers as Record<string, string> | undefined;
    return (name) => {
      const value = source?.[name.toLowerCase()];
      return typeof value === "string" ? value : null;
    };
  };

  instance.interceptors.response.use(
    (response) => {
      // O estado viaja de graça na resposta que a aba já pediu: é isso que substitui o poll.
      if (active && isSameOrigin(instance.getUri(response.config)))
        active.observeStatusHeaders(axiosHeaderReader(response.headers));
      return response;
    },
    async (error) => {
      const config = error.config;
      if (!active || !config || !isSameOrigin(instance.getUri(config))) return Promise.reject(error);

      active.observeStatusHeaders(axiosHeaderReader(error.response?.headers));
      const status = error.response?.status ?? 0;
      const marker = error.response?.headers?.[MAINTENANCE_HEADER.toLowerCase()] ?? null;
      active.observeResponse(config.method ?? "GET", status, marker, active.isAnnounced());

      const rejectedByPause = status === 503 && marker === "1";
      if (!rejectedByPause || !isSafeMethod(config.method) || config.__maintenanceRetried) return Promise.reject(error);

      config.__maintenanceRetried = true;
      const cancellation = new AbortController();
      const cancel = () => cancellation.abort();
      if (config.signal?.aborted) cancel();
      config.signal?.addEventListener?.("abort", cancel, { once: true });
      config.cancelToken?.subscribe(cancel);
      try {
        await active.waitForRecovery(cancellation.signal);
      } finally {
        config.signal?.removeEventListener?.("abort", cancel);
        config.cancelToken?.unsubscribe(cancel);
      }
      return instance.request(config);
    },
  );
}
